<template>
    <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <!-- Breadcrumb -->
        <nav class="bg-white border-b border-gray-200 shadow-sm">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="flex items-center space-x-2 py-3 text-sm overflow-x-auto whitespace-nowrap">
                    <router-link to="/" class="flex items-center text-gray-500 hover:text-[#ff7e41] transition-colors duration-200 flex-shrink-0">
                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"/>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m7 7l5-5 5 5"/>
                        </svg>
                        <span class="hidden sm:inline">Accueil</span>
                    </router-link>
                    <svg class="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                    <span class="font-semibold text-gray-900 flex items-center flex-shrink-0">
                        <svg class="w-4 h-4 mr-1 text-[#ff7e41]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                        </svg>
                        Auteurs
                    </span>
                </div>
            </div>
        </nav>

        <!-- Section Hero -->
        <section class="relative bg-pattern-svg py-12 sm:py-16 lg:py-20 overflow-hidden">
            <div class="absolute inset-0 opacity-10">
                <div class="absolute top-10 left-10 w-20 h-20 sm:w-32 sm:h-32 border-2 border-white rounded-full animate-pulse"></div>
                <div class="absolute top-20 right-10 w-16 h-16 sm:w-24 sm:h-24 border border-white rounded-full animate-pulse delay-75"></div>
                <div class="absolute bottom-10 left-1/4 w-12 h-12 sm:w-16 sm:h-16 border border-white rounded-full animate-pulse delay-150"></div>
            </div>

            <div class="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <div class="mb-4 sm:mb-6">
                    <span class="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs sm:text-sm font-semibold border border-white/30">
                        <svg class="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        Auteurs talentueux
                    </span>
                </div>

                <h1 class="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 tracking-tight">
                    Nos <span class="text-yellow-200">Auteurs</span>
                </h1>

                <p class="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-100 max-w-2xl md:max-w-3xl mx-auto leading-relaxed mb-6 sm:mb-8 font-light px-2">
                    Découvrez nos auteurs talentueux et plongez dans leurs univers uniques.
                </p>

                <!-- Statistiques Hero -->
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-8 sm:mt-12 max-w-2xl sm:max-w-4xl mx-auto">
                    <div class="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/20">
                        <div class="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2">{{ loading ? '...' : stats.totalBooks }}+</div>
                        <div class="text-xs sm:text-sm text-gray-200 font-medium">Ouvrages publiés</div>
                    </div>
                    <div class="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/20">
                        <div class="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2">{{ loading ? '...' : stats.totalCategories }}+</div>
                        <div class="text-xs sm:text-sm text-gray-200 font-medium">Ouvrages classiques</div>
                    </div>
                    <div class="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/20">
                        <div class="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2">{{ stats.totalStudents }}+</div>
                        <div class="text-xs sm:text-sm text-gray-200 font-medium">Travaux étudiants</div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Filtres et recherche -->
        <section class="bg-white border-b border-gray-200 py-4 sm:py-6">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                    <!-- Recherche  -->
                    <div class="flex-1 max-w-full lg:max-w-lg">
                        <div class="relative">
                            <input
                                type="text"
                                v-model="searchQuery"
                                @input="handleSearch"
                                placeholder="Rechercher un auteur..."
                                class="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-gray-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#ff7e41]/20 focus:border-[#ff7e41] transition-all duration-200 text-sm sm:text-base">
                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center">
                                <svg class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                                </svg>
                            </div>
                        </div>
                    </div>

                    <!-- Filtres  -->
                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                        <XSelect
                            v-model="selectedType"
                            @change="applyFilters"
                            :options="authorsOption"
                            class="text-sm"
                        />
                        <XSelect
                            v-model="sortBy"
                            @change="applyFilters"
                            :options="orderOption"
                            class="text-sm"
                        />
                        <!-- Vue grid/list  -->
                        <div class="grid grid-cols-2 gap-1.5 sm:gap-2 bg-gray-100 rounded-sm p-1">
                            <button
                                @click="viewMode = 'grid'"
                                :class="viewMode === 'grid' ? 'bg-white text-[#ff7e41] shadow-sm' : 'text-gray-500 hover:text-[#ff7e41]'"
                                class="p-2 flex items-center justify-center rounded-md transition-colors space-x-1 sm:space-x-2 text-xs sm:text-sm">
                                <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
                                </svg>
                                <span class="hidden sm:inline">Grille</span>
                            </button>
                            <button
                                @click="viewMode = 'list'"
                                :class="viewMode === 'list' ? 'bg-white text-[#ff7e41] shadow-sm' : 'text-gray-500 hover:text-[#ff7e41]'"
                                class="p-2 flex items-center justify-center rounded-md transition-colors space-x-1 sm:space-x-2 text-xs sm:text-sm">
                                <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
                                </svg>
                                <span class="hidden sm:inline">Liste</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Contenu principal  -->
        <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <!-- État de chargement  -->
            <div v-if="loading" class="text-center py-8 sm:py-12">
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    <div v-for="n in 6" :key="n" class="rounded-lg sm:rounded-xl border border-gray-100">
                        <div class="group bg-white rounded-lg sm:rounded-2xl shadow-lg overflow-hidden border border-gray-100 animate-pulse">
                            <!-- Skeleton loader content -->
                            <div class="h-24 sm:h-32 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"></div>
                            <div class="p-4 sm:p-6">
                                <div class="h-4 sm:h-5 bg-gray-200 rounded mb-2 sm:mb-3"></div>
                                <div class="h-3 sm:h-4 bg-gray-200 rounded w-1/2 mx-auto mb-3 sm:mb-4"></div>
                                <div class="h-8 sm:h-10 bg-gray-200 rounded-xl"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Résultats  -->
            <div v-else-if="authors.length">
                <!-- En-tête  -->
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-3 sm:gap-0">
                    <div>
                        <h2 class="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                            {{ authors.length }} auteur{{ authors.length > 1 ? 's' : '' }} trouvé{{ authors.length > 1 ? 's' : '' }}
                        </h2>
                        <p v-if="searchQuery" class="text-sm sm:text-base text-gray-600 mt-1">
                            Résultats pour "<strong>{{ searchQuery }}</strong>"
                        </p>
                    </div>
                    <div class="text-xs sm:text-sm text-gray-500">
                        Page {{ currentPage }} sur {{ totalPages }}
                    </div>
                </div>

                <!-- Grille-->
                <div v-if="viewMode === 'grid'" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-12 sm:mb-16">
                    <div v-for="author in authors" :key="author.id"
                         class="group bg-white rounded-lg sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 sm:hover:-translate-y-2 overflow-hidden border border-gray-100 hover:border-[#ff7e41]/30 cursor-pointer"
                         @click="goToAuthor(author.id)">

                        <!-- Bandeau  -->
                        <div class="relative h-20 sm:h-24 lg:h-32 bg-[#2A3992] overflow-hidden">
                            <div class="absolute inset-0 opacity-20">
                                <div class="absolute top-1 right-1 sm:top-2 sm:right-2 w-8 h-8 sm:w-12 sm:h-12 lg:w-16 lg:h-16 border border-white rounded-full"></div>
                                <div class="absolute bottom-1 left-1 sm:bottom-2 sm:left-2 w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12 border border-white rounded-full"></div>
                            </div>

                            <!-- Badge type -->
                            <div class="absolute top-2 left-2 sm:top-3 sm:left-3">
                                <span class="px-2 py-0.5 sm:px-3 sm:py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-bold rounded-full border border-white/30">
                                    {{ getAuthorType(author.author_type) }}
                                </span>
                            </div>
                        </div>

                        <!-- Avatar  -->
                        <div class="relative -mt-8 sm:-mt-10 lg:-mt-12 flex justify-center mb-3 sm:mb-4">
                            <div class="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full border-4 sm:border-6 border-white shadow-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                                <svg class="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/>
                                </svg>
                            </div>
                        </div>

                        <!-- Infos  -->
                        <div class="px-4 sm:px-6 pb-4 sm:pb-6 text-center">
                            <h3 class="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-1 sm:mb-2 group-hover:text-[#ff7e41] transition-colors">
                                {{ author.first_name }} {{ author.last_name }}
                            </h3>
                            <p class="text-[#ff7e41] text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                                {{ getAuthorType(author.author_type) }}
                            </p>

                            <!-- Statistiques  -->
                            <div class="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                                <div class="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{{ author.books.length ?? '0' }}</div>
                                <div class="text-xs sm:text-sm text-gray-600 font-medium">Ouvrage{{ (author.books.length ?? 0) > 1 ? 's' : '' }}</div>
                            </div>

                            <!-- Bouton  -->
                            <button @click.stop="goToAuthor(author.id)" class="w-full inline-flex items-center justify-center bg-gradient-to-r from-[#ff7e41] to-[#ff6b35] hover:from-[#ff6b35] hover:to-[#ff5722] text-white py-2 sm:py-2.5 px-3 sm:px-4 rounded-lg sm:rounded-xl font-semibold text-sm transition-all duration-300 hover:shadow-lg hover:scale-[1.02]">
                                <svg class="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"/>
                                </svg>
                                <span class="text-xs sm:text-sm">Voir les ouvrages</span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Liste  -->
                <div v-else-if="viewMode === 'list'" class="space-y-4 sm:space-y-6 mb-12 sm:mb-16">
                    <div v-for="author in authors" :key="author.id"
                         class="group bg-white rounded-lg sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-[#ff7e41]/30 cursor-pointer"
                         @click="goToAuthor(author.id)">

                        <div class="flex flex-col sm:flex-row items-start sm:items-center p-4 sm:p-6 gap-4 sm:gap-6">
                            <!-- Avatar  -->
                            <div class="flex-shrink-0 mx-auto sm:mx-0">
                                <div class="relative">
                                    <div class="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-white shadow-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                                        <svg class="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/>
                                        </svg>
                                    </div>
                                    <div class="absolute -top-1 -right-1">
                                        <span class="px-1.5 py-0.5 text-xs font-bold rounded-full bg-[#2A3992] text-white">
                                            {{ getAuthorType(author.author_type).substring(0, 3) }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- Infos  -->
                            <div class="flex-grow text-center sm:text-left min-w-0">
                                <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-0">
                                    <div class="min-w-0">
                                        <h3 class="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2 group-hover:text-[#ff7e41] transition-colors truncate">
                                            {{ author.first_name }} {{ author.last_name }}
                                        </h3>
                                        <p class="text-[#ff7e41] text-sm font-semibold mb-2 sm:mb-3">
                                            {{ getAuthorType(author.author_type) }}
                                        </p>

                                        <!-- Statistiques  -->
                                        <div class="flex items-center justify-center sm:justify-start space-x-4 sm:space-x-6 text-sm text-gray-600">
                                            <div class="flex items-center">
                                                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"/>
                                                </svg>
                                                <span class="font-semibold">{{ author.books.length ?? '0' }}</span>
                                                <span class="ml-1">ouvrage{{ (author.books.length ?? 0) > 1 ? 's' : '' }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Actions  -->
                                    <div class="flex items-center space-x-2 sm:space-x-3 mt-3 sm:mt-0">
                                        <button @click.stop="shareAuthor(author)" class="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center text-gray-500 hover:text-[#ff7e41] transition-colors flex-shrink-0">
                                            <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                                            </svg>
                                        </button>

                                        <button @click.stop="goToAuthor(author.id)" class="inline-flex items-center bg-gradient-to-r from-[#ff7e41] to-[#ff6b35] hover:from-[#ff6b35] hover:to-[#ff5722] text-white px-4 py-2 sm:px-6 sm:py-2.5 rounded-lg sm:rounded-xl font-semibold text-sm transition-all duration-300 hover:shadow-lg hover:scale-[1.02]">
                                            <span class="mr-1 sm:mr-2">Voir</span>
                                            <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pagination  -->
                <div class="mt-12 sm:mt-16">
                    <div class="bg-white rounded-xl shadow-lg max-w-xs sm:max-w-md mx-auto p-4 sm:p-6">
                        <div class="flex items-center justify-between mb-4 sm:mb-6">
                            <div class="text-xs text-gray-700">
                                Affichage
                                <span class="font-semibold">{{ paginationInfo.start }}</span>
                                à
                                <span class="font-semibold">{{ paginationInfo.end }}</span>
                                sur
                                <span class="font-semibold">{{ paginationInfo.total }}</span>
                                résultats
                            </div>
                        </div>

                        <div class="flex items-center justify-between">
                            <button @click="goToPreviousPage" :disabled="currentPage === 1"
                                    :class="{'opacity-50 cursor-not-allowed': currentPage === 1, 'hover:bg-gray-50 hover:text-[#ff7e41]': currentPage > 1}"
                                    class="flex items-center px-3 py-2 text-xs border border-gray-200 rounded-lg font-semibold transition-all duration-200">
                                <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                                </svg>
                                Précédent
                            </button>

                            <div class="hidden sm:flex items-center space-x-1">
                                <template v-for="page in visiblePages" :key="page">
                                    <button v-if="typeof page === 'number'" @click="goToPage(page)"
                                            :class="{'bg-[#ff7e41] text-white shadow-md': page === currentPage, 'text-gray-700 hover:bg-gray-50 hover:text-[#ff7e41]': page !== currentPage}"
                                            class="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded text-xs font-semibold transition-all duration-200-ml-2">
                                        {{ page }}
                                    </button>
                                    <span v-else class="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center text-gray-400 text-xs">
                                        {{ page }}
                                    </span>
                                </template>
                            </div>

                            <button @click="goToNextPage" :disabled="currentPage === totalPages"
                                    :class="{'opacity-50 cursor-not-allowed': currentPage === totalPages, 'hover:bg-gray-50 hover:text-[#ff7e41]': currentPage < totalPages}"
                                    class="flex items-center px-3 py-2 text-xs border border-gray-200 rounded-lg font-semibold transition-all duration-200 -ml-3">
                                Suivant
                                <svg class="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                                </svg>
                            </button>

                        </div>
                    </div>
                </div>
            </div>

            <!-- État vide  -->
            <div v-else class="text-center py-12 sm:py-20 px-4">
                <div class="bg-white rounded-lg sm:rounded-2xl shadow-lg p-6 sm:p-8 max-w-xs sm:max-w-sm mx-auto">
                    <div class="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
                        <svg class="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                        </svg>
                    </div>
                    <h3 class="text-base sm:text-lg font-bold text-gray-900 mb-2">Aucun auteur trouvé</h3>
                    <p class="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
                        <span v-if="searchQuery">Aucun résultat pour "{{ searchQuery }}".</span>
                        <span v-else>Il n'y a actuellement aucun auteur disponible.</span>
                    </p>
                    <button v-if="hasActiveFilters" @click="resetFilters"
                            class="w-full inline-flex items-center justify-center bg-[#ff7e41] text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold text-sm hover:bg-[#ff6b35] transition-colors">
                        Réinitialiser les filtres
                    </button>
                </div>
            </div>
        </main>
    </div>
</template>

<script setup>
import {ref, computed, onMounted, watch, reactive} from 'vue'

import XSelect from "@/vue/Form/XSelect.vue";
import {route} from "@/helper.js";

// Variables de pagination
const loading = ref(true)
const authors = ref([])
const pagination = ref({})
const searchQuery = ref('')
const selectedType = ref('')
const viewMode = ref('grid')
const sortBy = ref('name_asc')
const itemsPerPageSelected = ref(6)
const pageJump = ref('')

const getAuthorType=(type)=>{
    if(type==='1'){
        return 'Externe'
    }else {
        return 'Etudiant'
    }
}
console.log(authors.value)
const authorsOption=[
    {
        'id':1,
        'libelle':'Externe'
    },
    {
        'id':2,
        'libelle':'Etudiant'
    }
]
const orderOption=[
    {
        'id':'name_asc',
        'libelle':'Nom A-Z'
    },
    {
        'id':'name_desc',
        'libelle':'Nom Z-A'
    }
]

const changeItemsPerPage = () => {
    fetchAuthors(1, itemsPerPageSelected.value)
}


const jumpToPage = () => {
    const page = parseInt(pageJump.value)
    if (page && page >= 1 && page <= totalPages.value) {
        goToPage(page)
        pageJump.value = ''
    }
}
// Statistiques
const stats = reactive({
    totalBooks: 0,
    totalCategories: 0,
    totalStudents: 0
})

const currentPage = computed(() => pagination.value.current_page || 1)
const totalPages = computed(() => pagination.value.last_page || 1)
const itemsPerPage = computed(() => pagination.value.per_page || 6)
const totalItems = computed(() => pagination.value.total || 0)

const filteredAuthors = ref()


const visiblePages = computed(() => {
    const current = currentPage.value
    const total = totalPages.value
    const delta = 2
    const range = []
    const rangeWithDots = []

    // Si on a peu de pages, on affiche tout
    if (total <= 7) {
        for (let i = 1; i <= total; i++) {
            range.push(i)
        }
        return range
    }

    // Construction de la plage de pages
    for (let i = Math.max(2, current - delta);
         i <= Math.min(total - 1, current + delta);
         i++) {
        range.push(i)
    }

    // Ajout de la première page et des ellipses si nécessaire
    if (current - delta > 2) {
        rangeWithDots.push(1, '...')
    } else {
        rangeWithDots.push(1)
    }

    rangeWithDots.push(...range)

    if (current + delta < total - 1) {
        rangeWithDots.push('...', total)
    } else if (total > 1) {
        rangeWithDots.push(total)
    }

    // Suppression des doublons
    return rangeWithDots.filter((page, index, array) => array.indexOf(page) === index)
})

const paginationInfo = computed(() => {
    const start = ((currentPage.value - 1) * itemsPerPage.value) + 1
    const end = Math.min(currentPage.value * itemsPerPage.value, totalItems.value)
    return {
        start,
        end,
        total: totalItems.value,
        showing: end - start + 1
    }
})

const hasActiveFilters = computed(() => {
    return searchQuery.value || selectedType.value || sortBy.value !== 'name_asc'
})

const fetchAuthors = async (page = 1) => {
    loading.value = true
    try {
        // Construction des paramètres de requête
        const params = new URLSearchParams({
            page: page.toString(),
            per_page: '6'
        })
        if (searchQuery.value) {
            params.append('search', searchQuery.value)
        }
        if (selectedType.value) {
            params.append('type', selectedType.value)
        }
        if (sortBy.value) {
            params.append('sort', sortBy.value)
        }
        console.log(selectedType.value)
        const response = await axios.get(`/api/authorsData?${params.toString()}`)

        if (response.data && response.data.authors) {
            console.log(response.data.authors.data)
            pagination.value = response.data.authors
            authors.value = response.data.authors.data || []

            // Mise à jour des statistiques
            stats.totalBooks = response.data.books ?? 0
            stats.totalCategories = response.data.categories ?? 0
            stats.totalStudents = response.data.students ?? 0
        }

        // Mise à jour de l'URL sans rechargement
        const url = new URL(window.location)
        url.searchParams.set('page', page.toString())
        window.history.pushState({}, '', url.toString())

    } catch (error) {
        console.error('Erreur lors du chargement des auteurs:', error)
        NotificationToast('error', error.response?.data?.message || 'Erreur lors du chargement')
    } finally {
        loading.value = false
    }
}


const goToPage = (page) => {
    if (typeof page === 'number' && page >= 1 && page <= totalPages.value) {
        fetchAuthors(page)

        window.scrollTo({ top: 0, behavior: 'smooth' })
    }
}

const goToPreviousPage = () => {
    if (currentPage.value > 1) {
        goToPage(currentPage.value - 1)
    }
}


const goToNextPage = () => {
    if (currentPage.value < totalPages.value) {
        goToPage(currentPage.value + 1)
    }
}

const goToAuthor = (authorId) => {
    window.location.href = route('show-author', {author: authorId})
}

const shareAuthor = (author) => {
    if (navigator.share) {
        navigator.share({
            title: `${author.first_name} ${author.last_name}`,
            text: `Découvrez ${author.first_name} ${author.last_name}, ${author.author_type.toLowerCase()}`,
            url: window.location.href
        })
    } else {
        const text = `Découvrez ${author.first_name} ${author.last_name}, ${author.author_type.toLowerCase()} - ${window.location.href}`
        navigator.clipboard.writeText(text).then(() => {
            alert('Lien copié dans le presse-papiers !')
        })
    }
}
const resetFilters = () => {
    searchQuery.value = ''
    selectedType.value = ''
    sortBy.value = 'name_asc'
    fetchAuthors(1)
}


const applyFilters = () => {
    fetchAuthors(1)
}


let searchTimeout = null
const handleSearch = () => {
    if (searchTimeout) {
        clearTimeout(searchTimeout)
    }
    searchTimeout = setTimeout(() => {
        fetchAuthors(1)
    }, 200)
}


const initializePage = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const initialPage = parseInt(urlParams.get('page')) || 1

    searchQuery.value = urlParams.get('search') || ''
    selectedType.value = urlParams.get('type') || ''
    sortBy.value = urlParams.get('sort') || 'name_asc'

    fetchAuthors(initialPage)
}

watch([selectedType, sortBy,searchQuery], () => {
    applyFilters()
})

onMounted(() => {
    initializePage()
})

</script>

<style scoped>

@keyframes shimmer {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
}

.bg-shimmer {
    background-size: 200% 100%;
}

.shimmer-effect {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    animation: shimmer 0.5s ease-in-out infinite;
}

/* Animations personnalisées */
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.fade-in-up {
    animation: fadeInUp 0.6s ease-out;
}

/* Effet de survol sur les cartes d'auteurs */
.group:hover .group-hover\:scale-105 {
    transform: scale(1.05);
}

/* Transitions fluides pour les boutons */
button {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Effet de focus personnalisé */
.focus\:ring-2:focus {
    box-shadow: 0 0 0 3px rgba(255, 126, 65, 0.1);
}

/* Scrollbar personnalisée */
::-webkit-scrollbar {
    width: 8px;
}

::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

::-webkit-scrollbar-thumb {
    background: #ff7e41;
    border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
    background: #ff6b35;
}
</style>
